import RenegotiationAcceptedIcon from "@/assets/colorable/renegotiation_accepted.svg";
import RenegotiationRefusedIcon from "@/assets/colorable/renegotiation_refused.svg";
import UsersIcon from "@/assets/influencer_profile/7086FF_influencer_groups_gradient.svg";
import NewApplicationsIcon from "@/images/company_notification_icons/new_applications.svg";
import RatingIcon from "@/images/company_notification_icons/rating.svg";
import ReportingIcon from "@/images/company_notification_icons/reporting.svg";
import SendPackagesIcon from "@/images/company_notification_icons/send_packages.svg";
import StartCampaignIcon from "@/images/company_notification_icons/start_campaign.svg";
import {scrollSectionAtom} from "@/store/jotaiAtoms";
import type {TNotificationType} from "@/store/types";
import {getDefaultStore} from "jotai";

type NotificationParams = {
  CAMPAIGN_START_REMINDER: {campaign_id: number};
  CAMPAIGN_RENEGOTIATION_ACCEPTED: {campaign_id: number};
  CAMPAIGN_RENEGOTIATION_DECLINED: {campaign_id: number};
  CANCEL_REQUEST: {campaign_id: number};
  EXTENSION_REQUEST: {campaign_id: number};
  NEW_APPLICATIONS: {campaign_id: number};
  PERMISSION_UPGRADE_ACCEPTED: {company_id: number};
  PERMISSION_UPGRADE_REJECTED: {company_id: number};
  PERMISSION_UPGRADE_REQUEST: {company_id: number};
  RATING: {campaign_id: number; on_going: boolean};
  REPORT: {campaign_id: number; on_going: boolean};
  SEND_PACKAGES_REMINDER: {campaign_id: number; on_going: boolean};
};

type NotificationConfig<K extends TNotificationType> = {
  type: K;
  iconSrc: string;
  textClassName: string;
  iconTextTranslation: string;
  actionUrl: (params: NotificationParams[K]) => string;
};

const NotificationType: {
  [K in TNotificationType]: NotificationConfig<K>;
} = {
  CAMPAIGN_START_REMINDER: {
    type: "CAMPAIGN_START_REMINDER",
    iconSrc: StartCampaignIcon,
    textClassName: "text-warning-dark-yellow",
    iconTextTranslation: "start_your_campaign_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=overview`,
  },
  CAMPAIGN_RENEGOTIATION_ACCEPTED: {
    type: "CAMPAIGN_RENEGOTIATION_ACCEPTED",
    iconSrc: RenegotiationAcceptedIcon,
    textClassName: "text-success-herbal",
    iconTextTranslation: "renegotiation_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=applications`,
  },
  CAMPAIGN_RENEGOTIATION_DECLINED: {
    type: "CAMPAIGN_RENEGOTIATION_DECLINED",
    iconSrc: RenegotiationRefusedIcon,
    textClassName: "text-danger-raspberry",
    iconTextTranslation: "renegotiation_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=applications`,
  },
  CANCEL_REQUEST: {
    type: "CANCEL_REQUEST",
    iconSrc: UsersIcon,
    iconTextTranslation: "applicant_request",
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=preparation`,
  },
  EXTENSION_REQUEST: {
    type: "EXTENSION_REQUEST",
    iconSrc: UsersIcon,
    iconTextTranslation: "applicant_request",
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=active`,
  },
  NEW_APPLICATIONS: {
    type: "NEW_APPLICATIONS",
    iconSrc: NewApplicationsIcon,
    textClassName: "text-success-herbal",
    iconTextTranslation: "new_influencers_applied_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=overview`,
  },
  PERMISSION_UPGRADE_ACCEPTED: {
    type: "PERMISSION_UPGRADE_ACCEPTED",
    iconSrc: UsersIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "users_notification_type",
    actionUrl: params => {
      getDefaultStore().set(scrollSectionAtom, "companyMembers");
      return `/company/${params.company_id}/edit/`;
    },
  },
  PERMISSION_UPGRADE_REJECTED: {
    type: "PERMISSION_UPGRADE_REJECTED",
    iconSrc: UsersIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "users_notification_type",
    actionUrl: params => {
      getDefaultStore().set(scrollSectionAtom, "companyMembers");
      return `/company/${params.company_id}/edit/`;
    },
  },
  PERMISSION_UPGRADE_REQUEST: {
    type: "PERMISSION_UPGRADE_REQUEST",
    iconSrc: UsersIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "users_notification_type",
    actionUrl: params => {
      getDefaultStore().set(scrollSectionAtom, "companyMembers");
      return `/company/${params.company_id}/edit/`;
    },
  },
  RATING: {
    type: "RATING",
    iconSrc: RatingIcon,
    textClassName: "text-transparent bg-clip-text bg-linear-(--notification-gradient)",
    iconTextTranslation: "rate_your_influencers_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=${params.on_going ? "active" : "applications"}`,
  },
  REPORT: {
    type: "REPORT",
    iconSrc: ReportingIcon,
    textClassName: "text-primary-dark-solid",
    iconTextTranslation: "report_available_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=${params.on_going ? "active" : "applications"}`,
  },
  SEND_PACKAGES_REMINDER: {
    type: "SEND_PACKAGES_REMINDER",
    iconSrc: SendPackagesIcon,
    textClassName: "text-warning-dark-yellow",
    iconTextTranslation: "send_package_body_type",
    actionUrl: params => `/campaign/${params.campaign_id}/?tab=${params.on_going ? "preparation" : "overview"}`,
  },
} as const;

const useNotificationType = () => (type: TNotificationType) => NotificationType[type];

export default useNotificationType;
